import { cloneDeep } from 'lodash';
import { Message } from '@meitu/saas-ui';
import {
    addAdvancedTextMaterialId,
    getBehavior,
    getTrackFunc,
    parseLayerId,
    resetLayer,
    scaleLayers,
} from '@/framework';
import { Framework } from '@/framework/instance';
import { getLayerInfoWithRotate, isRectOutOfBounds } from '@/utils/layer-kit';
import { asyncTraverseTrees, traverseTree } from '@/utils/tree-kit';
import {
    COPY_DISTANCE,
    ISize,
    LayerType,
    MAX_STAGES,
    MAX_STAGES_TIP,
    type IExtraInfo,
    type ILayerProps,
    type IPoint,
} from '@/version-types';
import { ClipboardPluginDependency } from './dependency';
import { readClipboardData, writeClipboardData } from './io';
import { IClipboardData, IClipboardHotkeyType, IClipboardLayer, IClipboardPage, IClipboardTemplate } from './types';
import { isUseSystemClipboard, removeAIPPTExtraInfo } from './utils';

function makeSurePointInStage(point: IPoint, layer: ILayerProps, stageSize: ISize) {
    const layerBounding = {
        left: point.x,
        top: point.y,
        width: layer.width,
        height: layer.height,
        rotate: layer.rotate,
    };

    const stageCenterPosition = {
        x: (stageSize.width - layer.width) / 2,
        y: (stageSize.height - layer.height) / 2,
    };

    // 如果粘贴的图层不在画布内，则调整到画布的中心
    const newLayerInfo = getLayerInfoWithRotate(layerBounding);

    // 计算图层是否在舞台中
    if (!isRectOutOfBounds(newLayerInfo, stageSize)) {
        return point;
    }

    return stageCenterPosition;
}

export interface ClipboardPluginConfig {
    allowPasteLayerTypes: LayerType[];
    disablePasteStage: boolean;
}

export class ClipboardPlugin extends Framework {
    // 粘贴依赖的信息
    private pasteInfo: {
        originIndex: number;
        // 记录相对图层（单图层或者多选的临时组）的图层原始位置，用户获取粘贴的相对位置
        left: number;
        top: number;
        // 这个主要用来解决跨页面粘贴时，不同画布尺寸需要重新计算粘贴位置
        extraInfo: IExtraInfo;
        // 记录相对图层或者临时组内图层的原始位置和 id，用来判断移动和删除
        layers: Array<{
            id: string;
            left: number;
            top: number;
        }>;
    } | null = null;

    public config: ClipboardPluginConfig = {
        allowPasteLayerTypes: [],
        disablePasteStage: false,
    };

    getClipboardData(e: ClipboardEvent | null) {
        return readClipboardData(e);
    }

    private setPasteInfo({ layer, extraInfo }: IClipboardLayer) {
        const originIndex = parseLayerId(layer.id).originIndex;

        if (originIndex === null) return;

        this.pasteInfo = {
            originIndex,
            left: layer.left,
            top: layer.top,
            extraInfo,
            // ATTENTION: 多选的临时组需要记录所有的一级子图层，用来判断是否移动或者删除，因为临时组在取消多选时会丢失
            layers: this.framework.checkIsExtraGroup(layer)
                ? layer.children.map((child) => ({
                      id: child.id,
                      left: child.left,
                      top: child.top,
                  }))
                : [
                      {
                          id: layer.id,
                          left: layer.left,
                          top: layer.top,
                      },
                  ],
        };
    }

    /**
     * 设置拷贝信息
     * 原来叫做 setCopyInfo
     * @param template - 拷贝信息
     * @returns
     */
    async setClipboardData({ template, text }: { template?: IClipboardTemplate; text?: string }) {
        const { framework } = this;

        if (!template && !text) return;

        // 提供系统剪贴板的纯文本的写入逻辑，同时清空自定义剪贴板
        if (text) {
            writeClipboardData(text);
            this.pasteInfo = null;
            this.emitClipboardChange(null);
            return;
        }

        if (!template) return;

        const copyTemplate = cloneDeep(template);
        const isCopyPage = Array.isArray(copyTemplate);
        const flattenLayers = Array.isArray(copyTemplate)
            ? copyTemplate.map((page) => page.layer).flat()
            : [copyTemplate.layer];

        // 复制前的图层数据清理
        await asyncTraverseTrees(flattenLayers, async (item: ILayerProps) => {
            if (!isCopyPage) {
                removeAIPPTExtraInfo(item);
            }
            // 删除 ai 生成操作
            delete item.aiGcOperate;
            await getBehavior(framework.identity, item)?.convertCodeToHttpUrl?.(item);
        });

        if (!Array.isArray(copyTemplate)) {
            this.setPasteInfo(copyTemplate);
        }

        writeClipboardData(copyTemplate, true);

        this.emitClipboardChange(template);
    }

    private async copyStage(stageSelection: number[], e?: ClipboardEvent | null) {
        const { framework } = this;

        // ATTENTION: 判断来自系统事件的复制，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('copy', true);

        const clipboardPages: IClipboardPage[] = [];
        for (const index of stageSelection) {
            const isImageEditor = framework.getMainLayer(index);
            // 图片编辑器暂不支持页面复制
            if (isImageEditor) continue;
            const layer = framework.stage.cloneStage(index);
            clipboardPages.push({
                layer,
                extraInfo: framework.getExtraInfo(index),
            });
        }

        await this.setClipboardData({ template: clipboardPages });
    }

    private async copyLayer(layer: ILayerProps, e?: ClipboardEvent | null) {
        const { framework } = this;
        const activeStageIndex = framework.stage.getActiveStageIndex();
        const activeExtraInfo = framework.getExtraInfo(activeStageIndex);

        if (!layer) return;

        const tempLayer = cloneDeep(layer);
        const wrapLayer = framework.getWrapLayer(layer.id, true);

        // 拷贝剪切组内元素，需要校验花字素材id
        addAdvancedTextMaterialId(tempLayer, wrapLayer);

        // ATTENTION: 判断来自系统事件的复制，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('copy', false);

        const clipboardLayer: IClipboardLayer = {
            layer: tempLayer,
            extraInfo: activeExtraInfo,
        };

        // 记录粘贴的相对图层为源图层
        this.setPasteInfo(clipboardLayer);
        await this.setClipboardData({ template: clipboardLayer });
    }

    async copy(e?: ClipboardEvent | null) {
        const { framework } = this;
        const activeInfo = framework.operate.getActiveInfo()?.info;
        const stageSelection = framework.stage.getStageSelection();

        // 复制整页
        if (!activeInfo && stageSelection.length) {
            await this.copyStage(stageSelection, e);
            return;
        }

        if (
            activeInfo &&
            !activeInfo.lock &&
            !framework.getBehavior(activeInfo.layerType as LayerType)?.checkDisableCopy?.()
        ) {
            const layer = framework.getLayer(activeInfo.pickId || activeInfo.id);

            await this.copyLayer(layer, e);
            return;
        }
    }

    private async cutStage(stageSelection: number[]) {
        const { framework } = this;
        const clipboardPages: IClipboardPage[] = [];
        for (const index of stageSelection) {
            const isImageEditor = framework.getMainLayer(index);
            // 图片编辑器暂不支持页面复制
            if (isImageEditor) continue;
            const layer = framework.stage.cloneStage(index);
            clipboardPages.push({
                layer,
                extraInfo: framework.getExtraInfo(index),
            });
        }

        const res = this.framework.getInstance(ClipboardPluginDependency).deleteStages(stageSelection);

        if (!res) return;

        await this.setClipboardData({ template: clipboardPages });
    }

    private cutLayer(layer: ILayerProps, e?: ClipboardEvent | null) {
        if (!layer) return;

        if (!this.framework.checkPossibleCut(layer)) {
            return;
        }

        const { framework } = this;
        const activeStageIndex = framework.stage.getActiveStageIndex();
        const activeExtraInfo = framework.getExtraInfo(activeStageIndex);
        const delLayerId = layer.id;
        const wrapLayer = framework.getWrapLayer(delLayerId, true);

        this.framework.getInstance(ClipboardPluginDependency).deleteLayer(delLayerId);

        // 拷贝剪切组内元素，需要校验花字素材id
        addAdvancedTextMaterialId(layer, wrapLayer);

        // ATTENTION: 判断来自系统事件的剪切，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('cut', false);

        const clipboardLayer: IClipboardLayer = {
            layer,
            extraInfo: activeExtraInfo,
        };

        // 记录粘贴的相对图层为源图层
        this.setPasteInfo(clipboardLayer);
        this.setClipboardData({ template: clipboardLayer });
    }

    async cut(e?: ClipboardEvent | null) {
        const { framework } = this;
        const activeInfo = framework.operate.getActiveInfo()?.info;
        const stageSelection = framework.stage.getStageSelection();

        // 剪切页面
        if (!activeInfo && stageSelection.length) {
            await this.cutStage(stageSelection);
            return;
        }

        if (
            activeInfo &&
            !activeInfo.lock &&
            !framework.getBehavior(activeInfo.layerType as LayerType)?.checkDisableCut?.()
        ) {
            const delLayerId = activeInfo.pickId || activeInfo.id;
            const delLayer = framework.getLayer(delLayerId);

            this.cutLayer(delLayer, e);
            return;
        }
    }

    private getPastedPoint(
        copyLayer: ILayerProps,
        copyExtraInfo: IExtraInfo,
        copyCenterPoint?: IPoint,
        isUseOriginalPosition?: boolean,
    ) {
        const { framework } = this;
        const activeStageIndex = framework.stage.getActiveStageIndex();
        const activeExtraInfo = framework.getExtraInfo(activeStageIndex);

        // 指定位置粘贴，比如编辑器自定义的右键菜单的粘贴入口，优先级最高
        if (copyCenterPoint) {
            return copyCenterPoint;
        }

        const sourcePoint = {
            x: copyLayer.left,
            y: copyLayer.top,
        };

        // 强制按原位置粘贴
        if (isUseOriginalPosition) {
            return sourcePoint;
        }

        // ATTENTION: 跨浏览器窗口复制时，优先读当前窗口的画布信息，没有读取源窗口的画布信息
        const extraInfo = this.pasteInfo?.extraInfo || copyExtraInfo;

        if (!activeExtraInfo.width || !activeExtraInfo.height) {
            return sourcePoint;
        }

        // 粘贴的画布尺寸和复制的画布尺寸不同，直接按当前画布居中粘贴
        if (extraInfo?.width !== activeExtraInfo.width || extraInfo?.height !== activeExtraInfo.height) {
            return {
                x: activeExtraInfo.width / 2 - copyLayer.width / 2,
                y: activeExtraInfo.height / 2 - copyLayer.height / 2,
            };
        }

        const translatePastedPoint = this.getTranslatePastedPoint(sourcePoint);

        // 确保粘贴的点在画布内
        return makeSurePointInStage(translatePastedPoint, copyLayer, {
            width: activeExtraInfo.width,
            height: activeExtraInfo.height,
        });
    }

    private getTranslatePastedPoint(sourcePoint: IPoint) {
        const { framework } = this;
        const activeStageIndex = framework.stage.getActiveStageIndex();

        // 容错处理
        if (!this.pasteInfo) return sourcePoint;

        // 当前画布原始索引
        const currentIndex = framework.convertStageToOrigin(activeStageIndex);
        // 前一个粘贴图层的原始画布索引
        const preIndex = this.pasteInfo.originIndex;

        // 粘贴在不同画布上不需要调整距离，按源图层位置粘贴
        if (currentIndex !== preIndex) {
            return sourcePoint;
        }

        const layerIds = this.pasteInfo.layers.map((layer) => layer.id);
        const hasLayerDeleted = layerIds.some((id) => !framework.getLayer(id));

        // 相对图层被删除，按源图层位置粘贴
        if (hasLayerDeleted) {
            return sourcePoint;
        }

        const hasLayerMoved = this.pasteInfo.layers.some((layer) => {
            const relativeLayer = framework.getLayer(layer.id);

            return relativeLayer.left !== layer.left || relativeLayer.top !== layer.top;
        });

        // 相对图层被移动，按源图层位置粘贴
        if (hasLayerMoved) {
            return sourcePoint;
        }

        return {
            x: this.pasteInfo.left + COPY_DISTANCE,
            y: this.pasteInfo.top + COPY_DISTANCE,
        };
    }

    /**
     * 粘贴图层，并激活
     * @param copyLayer - 拷贝的图层
     * @param copyCenterPoint - 拷贝图层的中心点
     * @param copyExtraInfo - 拷贝图层的extraInfo
     * @param isUseOriginalPosition - 是否使用原位置粘贴
     * @returns
     */
    private pasteLayer(
        copyLayer: ILayerProps,
        copyExtraInfo: IExtraInfo,
        {
            point: copyCenterPoint,
            isUseOriginalPosition,
        }: {
            point?: IPoint;
            isUseOriginalPosition?: boolean;
        },
    ) {
        // 容错处理
        if (!copyLayer || copyLayer.lock || copyLayer.parentId) return;

        const { framework } = this;
        const activeStageIndex = framework.stage.getActiveStageIndex();
        const activeExtraInfo = framework.getExtraInfo(activeStageIndex);

        // 从付费模板粘贴素材到免费模板，需将免费模板变成付费模板
        const needSetVip = !activeExtraInfo.threshold && !!copyExtraInfo?.threshold;
        const newExtraInfoInfo = needSetVip ? { ...activeExtraInfo, threshold: 1 } : { ...activeExtraInfo };

        const pastedPoint = this.getPastedPoint(copyLayer, copyExtraInfo, copyCenterPoint, isUseOriginalPosition);
        const newInfo = cloneDeep(copyLayer);

        resetLayer(newInfo, pastedPoint.x - copyLayer.left, pastedPoint.y - copyLayer.top);

        traverseTree(newInfo, (layer: ILayerProps) => {
            removeAIPPTExtraInfo(layer);
        });

        this.framework.getInstance(ClipboardPluginDependency).addLayer(newInfo, {
            isActiveNewLayer: true,
            isWriteHistory: true,
            trackFunc: getTrackFunc(framework.identity, copyLayer, 'copy'),
            extraInfo: {
                oldExtraInfo: activeExtraInfo,
                newExtraInfo: newExtraInfoInfo,
            },
        });

        // 粘贴后，相对图层改为新粘贴的图层
        this.setPasteInfo({
            layer: newInfo,
            extraInfo: newExtraInfoInfo,
        });
        return true;
    }

    private pasteStage(pagesPasted: IClipboardPage[]) {
        const { framework } = this;
        const originIndex = framework.stage.getActiveOriginIndex();
        const isImageEditor = framework.getMainLayer(originIndex);
        // 图片编辑器暂不支持页面粘贴
        if (isImageEditor) return;

        const index = framework.getRenderStageLength();
        if (index >= MAX_STAGES) {
            Message.warning(MAX_STAGES_TIP());
            return;
        }

        const stages: ILayerProps[][] = pagesPasted.map((page) => page.layer);
        const extraInfos: IExtraInfo[] = pagesPasted.map((page) => page.extraInfo);
        const activeStageIndex = framework.stage.getActiveStageIndex();
        const activeExtraInfo = framework.getExtraInfo(activeStageIndex);
        const isLongImageMode = framework.stage.isLongImageMode();

        stages.forEach((stage, index) => {
            const currentExtraInfo = extraInfos[index];
            if (
                isLongImageMode &&
                activeExtraInfo.width &&
                activeExtraInfo.height &&
                currentExtraInfo.width &&
                currentExtraInfo.height &&
                currentExtraInfo.width !== activeExtraInfo.width
            ) {
                const oldSize = {
                    width: currentExtraInfo.width,
                    height: currentExtraInfo.height,
                };
                const ratio = currentExtraInfo.width / currentExtraInfo.height;

                currentExtraInfo.width = activeExtraInfo?.width;
                currentExtraInfo.height = currentExtraInfo.width / ratio;

                scaleLayers(framework.identity, stage, oldSize, {
                    width: currentExtraInfo.width,
                    height: currentExtraInfo.height,
                });
            }
        });

        const selectedStageIndexes = framework.stage.getStageSelection();
        // 粘贴到多画布舞台时需要进行缩放处理
        this.framework
            .getInstance(ClipboardPluginDependency)
            .addStages(stages, extraInfos, true, false, Math.max.apply(null, selectedStageIndexes));
    }

    paste(e?: ClipboardEvent | null, point?: IPoint) {
        const clipboardData = readClipboardData(e);

        // 粘贴的带/t和/n就按表格处理
        const clipboardTextIsTableInfo =
            (clipboardData.text.includes('\t') && clipboardData.text.includes('\n') && !clipboardData.html) ||
            clipboardData.html.includes('<table');

        // 先处理表格再处理 file (wps excel粘贴出来的既有file又有文本)
        if (clipboardTextIsTableInfo) {
            this.framework.operate.globalMask.show({ loading: true });
            this.framework
                .getInstance(ClipboardPluginDependency)
                .addTable(clipboardData)
                .finally(() => {
                    this.framework.operate.globalMask.hide();
                });
            return;
        }

        if (clipboardData.files.length) {
            // 文件粘贴上传
            this.emitPasteFile(clipboardData.files);
            return;
        }

        let layerPasted: IClipboardLayer | undefined;
        let pagesPasted: IClipboardPage[] | undefined;

        if (clipboardData.template) {
            // 判断是否为多舞台数据格式
            const isMultiStageFormat = Array.isArray(clipboardData.template);

            if (isMultiStageFormat) {
                pagesPasted = clipboardData.template as IClipboardPage[];
            } else {
                layerPasted = clipboardData.template as IClipboardLayer;
            }
        }

        if (isUseSystemClipboard(clipboardData)) {
            this.framework.getInstance(ClipboardPluginDependency).addText(clipboardData);
            // ATTENTION: 判断来自系统事件的粘贴，通知业务侧用来埋点
            if (e) this.emitHotkeyEvent('paste', false);
            return;
        }

        // 页面粘贴
        if (pagesPasted) {
            this.pasteStage(pagesPasted);
            // ATTENTION: 判断来自系统事件的粘贴，通知业务侧用来埋点
            if (e) this.emitHotkeyEvent('paste', true);
            return;
        }

        if (!layerPasted) return;

        // HACK: 没有 clipboardData 的粘贴事件，来自触发原位置粘贴事件
        this.pasteLayer(layerPasted.layer, layerPasted.extraInfo, { point, isUseOriginalPosition: !e?.clipboardData });
        // ATTENTION: 判断来自系统事件的粘贴，通知业务侧用来埋点
        if (e) this.emitHotkeyEvent('paste', false);
    }

    private static pasteFileEventName = 'plugin-clipboard-paste-file';

    private emitPasteFile(files: File[]) {
        this.framework.events.emit(ClipboardPlugin.pasteFileEventName, files);
    }

    onPasteFile(fn: (files: File[]) => void) {
        const handle = this.framework.events.on(ClipboardPlugin.pasteFileEventName, fn);

        return () => {
            this.framework.events.off(handle);
        };
    }

    private static clipboardChangeEventName = 'plugin-clipboard-change';

    private emitClipboardChange(template: IClipboardTemplate) {
        this.framework.events.emit(ClipboardPlugin.clipboardChangeEventName, { template });
    }

    /** ATTENTION: 当前因为还未做系统剪贴板的权限申请机制，因此只有自定义剪贴板变化才有触发事件 */
    onClipboardChange(fn: (clipboardData: IClipboardData) => void) {
        const handle = this.framework.events.on(ClipboardPlugin.clipboardChangeEventName, fn);

        return () => {
            this.framework.events.off(handle);
        };
    }

    private static hotkeyEventName = 'plugin-clipboard-hotkey-event';

    private emitHotkeyEvent(keyType: IClipboardHotkeyType, isPage: boolean) {
        this.framework.events.emit(ClipboardPlugin.hotkeyEventName, { keyType, isPage });
    }

    onHotkeyEvent(fn: (keyType: IClipboardHotkeyType, isPage: boolean) => void) {
        const handle = this.framework.events.on(ClipboardPlugin.hotkeyEventName, ({ keyType, isPage }) => {
            fn(keyType, isPage);
        });

        return () => {
            this.framework.events.off(handle);
        };
    }
}
